import { Button, Grid, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useEffect } from 'react';

import { Transit, TransitGroup } from '@e-origin/shared';

import { LeavePageNotification, StickyContainer, TextInput } from '@components';
import { getAmendmentType } from '../../utils';
import { AdditionalSupplyChainActor } from './ AdditionalSupplyChainActor';
import { CarrierSection } from './CarrierSection';
import { RepresentativeSection } from './RepresentativeSection';
import { StakeholderSection } from './StakeholderSection';
import { useTransitViewStakeholdersFormInitializer } from './useTransitViewStakeholdersFormInitializer';

interface TransitViewStakeholdersProps {
  transit: Partial<Transit>;
  editDisabled: boolean;
}

export const TransitViewStakeholders: React.FC<TransitViewStakeholdersProps> = ({ transit, editDisabled }) => {
  const { isGeneralAmendment, isGuaranteeAmendment } = getAmendmentType(transit.TransitOperation.amendment);

  const openOverwriteMessage = () => {
    modals.openConfirmModal({
      title: 'Confirm save',
      children: (
        <Text>There are some headers values that had been defined. Saving will overwrite the lower level values. Are you sure you want to continue?</Text>
      ),
      labels: { confirm: 'Continue', cancel: 'Cancel' },
      onConfirm: handleOverwriteConfirm,
    });
  };

  const { form, onSubmit, buildFormValues } = useTransitViewStakeholdersFormInitializer({
    transit,
    openOverwriteMessage,
  });
  const handleOverwriteConfirm = () => onSubmit(form.values, true);
  const isArrival = form.values.generalInfo?.group === TransitGroup.ARRIVAL;

  useEffect(() => {
    form.setValues(buildFormValues(transit));
    form.resetDirty({});
  }, [transit]);

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <ScrollArea h="100%" offsetScrollbars="y">
        <form onSubmit={form.onSubmit((values) => onSubmit(values, false))}>
          <Stack mx="xl">
            {isArrival && (
              <Stack mb="xl">
                <Title order={5} fw={600}>
                  Trade at destination
                </Title>
                <Grid>
                  <Grid.Col span={4}>
                    <TextInput
                      {...form.getInputProps('TraderAtDestination.identificationNumber')}
                      label="Identification number"
                      disabled={isGuaranteeAmendment || editDisabled}
                    />
                  </Grid.Col>
                </Grid>
              </Stack>
            )}

            <StakeholderSection form={form} editDisabled={editDisabled || isArrival} type="Consignor" />
            <StakeholderSection form={form} editDisabled={editDisabled || isArrival} type="Consignee" />
            <StakeholderSection form={form} editDisabled={isGeneralAmendment || editDisabled || isArrival} type="HolderOfTheTransitProcedure" />
            <RepresentativeSection form={form} editDisabled={isGeneralAmendment || isGuaranteeAmendment || editDisabled || isArrival} />

            {!isArrival && (
              <>
                <CarrierSection form={form} editDisabled={isGuaranteeAmendment || editDisabled} />
                <AdditionalSupplyChainActor form={form} editDisabled={isGuaranteeAmendment || editDisabled} />
              </>
            )}
          </Stack>

          <StickyContainer>
            <Button type="submit" disabled={(editDisabled && !isGuaranteeAmendment) || !form.isValid()} variant="filled" size="lg" data-pw="save-transit-btn">
              Save
            </Button>
          </StickyContainer>
        </form>
      </ScrollArea>
    </>
  );
};
